import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PageWrapper } from '../components/layout/PageWrapper';
import { OrnamentDivider } from '../components/ui/OrnamentDivider';
import { useCreateNews } from '../hooks/useNews';
import { useAuthStore } from '../store/authStore';
import { isFreeAdmin } from '../lib/freeAdmins';

function MarkdownField({
  label,
  value,
  onChange,
  placeholder,
  required,
  rows = 6,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  rows?: number;
}) {
  const [preview, setPreview] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase">
          {label}{required && ' *'}
        </label>
        <button
          type="button"
          onClick={() => setPreview((v) => !v)}
          className="font-mono text-xs text-mafia-muted hover:text-mafia-gold transition-colors tracking-wider uppercase"
        >
          {preview ? 'Редактировать' : 'Предпросмотр'}
        </button>
      </div>
      {preview ? (
        <div className="min-h-[120px] border border-mafia-border bg-mafia-dark p-4 text-sm text-mafia-paper-dim leading-relaxed">
          {value ? (
            <Markdown remarkPlugins={[remarkGfm]}>{value}</Markdown>
          ) : (
            <span className="text-mafia-muted italic">Пусто</span>
          )}
        </div>
      ) : (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          rows={rows}
          className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-3 font-mono resize-y focus:outline-none focus:border-mafia-gold transition-colors placeholder:text-mafia-muted"
        />
      )}
    </div>
  );
}

export function AdminCreateNewsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const createNews = useCreateNews();

  const [title, setTitle] = useState('');
  const [teaser, setTeaser] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [breakdown, setBreakdown] = useState('');
  const [error, setError] = useState('');

  if (!isFreeAdmin(user?.email)) {
    return (
      <PageWrapper>
        <p className="text-mafia-paper-dim">Нет доступа.</p>
      </PageWrapper>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const item = await createNews.mutateAsync({
        title: title.trim(),
        teaser: teaser.trim(),
        videoUrl: videoUrl.trim(),
        ...(breakdown.trim() ? { breakdown: breakdown.trim() } : {}),
      });
      navigate(`/news/${item.newsId}`);
    } catch (err: unknown) {
      setError((err as Error).message || 'Не удалось создать новость.');
    }
  }

  return (
    <PageWrapper className="max-w-3xl">
      <Link to="/news" className="font-mono text-xs text-mafia-muted hover:text-mafia-gold tracking-wider uppercase transition-colors mb-8 inline-block">
        ← Все новости
      </Link>

      <p className="font-mono text-xs text-mafia-gold tracking-[0.3em] uppercase mb-3">◆ Админ ◆</p>
      <h1 className="font-display text-4xl font-bold text-mafia-paper mb-8">Добавить новость</h1>

      <OrnamentDivider className="mb-8" />

      <form onSubmit={handleSubmit} className="space-y-7">
        {/* Заголовок */}
        <div>
          <label className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-2 block">
            Заголовок *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="1 игра 13 июня 2026"
            required
            className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-3 focus:outline-none focus:border-mafia-gold transition-colors placeholder:text-mafia-muted"
          />
        </div>

        {/* Анонс */}
        <MarkdownField
          label="Анонс"
          value={teaser}
          onChange={setTeaser}
          placeholder="Краткое описание игры, отображается в карточке новости..."
          required
          rows={5}
        />

        {/* Видео */}
        <div>
          <label className="font-mono text-xs text-mafia-gold tracking-[0.2em] uppercase mb-2 block">
            Видео — после раздачи ролей *
          </label>
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=...&t=123s"
            required
            className="w-full bg-mafia-dark border border-mafia-border text-mafia-paper text-sm p-3 font-mono focus:outline-none focus:border-mafia-gold transition-colors placeholder:text-mafia-muted"
          />
          <p className="text-mafia-muted text-xs font-mono mt-1">
            Таймкод в ссылке будет учтён. Полное видео добавьте ссылкой в разборе игры.
          </p>
        </div>

        {/* Разбор */}
        <MarkdownField
          label="Разбор игры (спойлеры, необязательно)"
          value={breakdown}
          onChange={setBreakdown}
          placeholder="Подробный разбор: кто был мафией, ключевые моменты..."
          rows={10}
        />

        {error && (
          <p className="text-mafia-crimson-light font-mono text-sm">{error}</p>
        )}

        <div className="flex gap-4 pt-2">
          <button
            type="submit"
            disabled={createNews.isPending}
            className="btn-gold-solid flex-1 py-3 text-sm disabled:opacity-50"
          >
            {createNews.isPending ? 'Публикуем...' : 'Опубликовать'}
          </button>
          <Link
            to="/news"
            className="flex-1 py-3 text-sm text-center font-mono border border-mafia-border text-mafia-paper-dim hover:text-mafia-paper transition-colors"
          >
            Отмена
          </Link>
        </div>
      </form>
    </PageWrapper>
  );
}
